import React, { type JSX } from 'react';

import { BackdropLoader, CustomModal, CustomTransComponent, DeleteModal } from '../../../components';
import { useConfirm } from '@fiji/common/src/hooks';
import { Icon, List, Radio, Stack, TextField, Typography } from '@mui/material';
import { ModalActions } from '../../../components/CustomModal';
import { InfoListItem } from '@brightlayer-ui/react-components';
import {
    useDeleteDashboardMutation,
    useLazyGetAllDashboardsQuery,
    useUpdateDashboardMutation,
} from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { useDispatch } from 'react-redux';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import Link from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import { Search } from '@mui/icons-material';
import { useDebounce, useIsMount } from 'hooks';
import { GroupHierarchySkeleton } from 'pages/GroupHierarchy/GroupHierarchySkeleton';

const getDeleteFilters = (dashboardData: any): Record<string, unknown> => {
    const filters: Record<string, unknown> = {
        status: ['INACTIVE'],
    };

    if (dashboardData?.assignType === 'SOURCE') {
        filters.assignType = [dashboardData?.assignType];
        filters.sourceId = dashboardData?.sourceId;
    }

    if (dashboardData?.assignType === 'GROUP') {
        filters.groupId = dashboardData?.assignTypeIds[0];
    }

    if (dashboardData?.assignType === 'DEVICE') {
        filters.deviceId = dashboardData?.assignTypeIds[0];
    }

    return filters;
};

const getDeleteModalState = (dashboardData: any, recordCount: number): string => {
    if (dashboardData?.status === 'INACTIVE') {
        return 'Delete';
    }

    return recordCount ? 'Assign' : 'EmptyModal';
};

type DashboardAssignListItemProps = {
    item: any;
    index: number;
    selectedDashboardId: any;
    setSelectedDashboard: (dashboard: any) => void;
    getSelectedNode: (node: any) => any;
};

const DashboardAssignListItem = ({
    item,
    index,
    selectedDashboardId,
    setSelectedDashboard,
    getSelectedNode,
}: DashboardAssignListItemProps): JSX.Element => {
    const itemId = item?.id;
    const itemName = item?.name;
    const itemSubtitle = item?.sourceName ?? item?.assignType;
    const itemType = item?.isSystemDashboard ? 'System' : 'User';
    const isSelected = itemId === selectedDashboardId;

    return (
        <InfoListItem
            key={itemId}
            divider="full"
            hidePadding
            data-testid="delete-dashboard-assign-list"
            title={getSelectedNode(item)}
            subtitle={itemSubtitle}
            leftComponent={
                <Stack mr={3} minWidth={'170px'}>
                    <Typography variant="body2" fontSize={'14px'} fontWeight={'600'}>
                        {itemName}
                    </Typography>
                    <Typography variant="body2" fontSize={'14px'} fontWeight={'400'}>
                        {itemType}
                    </Typography>
                </Stack>
            }
            rightComponent={
                <>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography variant="body2" fontWeight={'400'} fontSize={'14px'} mr={1}>
                            {item?.associatedCount}
                        </Typography>
                        <Icon>
                            <Link></Link>
                        </Icon>
                    </Stack>
                    <Stack ml={2}>
                        <Radio
                            data-testid={`delete-dashboard-radio-${index}`}
                            checked={isSelected}
                            onChange={(): void => setSelectedDashboard(item)}
                            value={itemId}
                            id={itemName}
                            name="radio-buttons"
                        />
                    </Stack>
                </>
            }
            classes={{ title: 'minWidth' }}
        />
    );
};

export const DeleteDashboardModal = React.forwardRef((props: any, ref): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMount = useIsMount();
    const [selectedDashboard, setSelectedDashboard] = React.useState<any>({});
    const [activeModal, setActiveModal] = React.useState('');
    const [selectedData, setSelectedData] = React.useState<any>(null);
    const [deleteDashboard, { isLoading: isDeletingDashboard }] = useDeleteDashboardMutation();
    const [updateDashboard, { isLoading: isUpdatingDashboard }] = useUpdateDashboardMutation();
    const [searchKey, setSearchKey] = React.useState('');
    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);

    const closeModal = (): void => {
        setSearchKey('');
        setActiveModal('');
    };

    const [getFilter, { data, isLoading, isFetching }]: any = useLazyGetAllDashboardsQuery();

    React.useEffect(() => {
        if (!isMount && activeModal === 'Assign') {
            getFilter({
                body: {
                    page: 0,
                    size: 10,
                    ...(!isMount && { searchKey: debouncedValue }),
                    filters: {
                        status: ['INACTIVE'],
                        ...(selectedData?.assignType === 'GROUP'
                            ? { groupId: selectedData?.assignTypeIds[0] }
                            : selectedData?.assignType === 'SOURCE'
                            ? { sourceId: selectedData?.sourceId }
                            : { deviceId: selectedData?.assignTypeIds[0] }),
                    },
                },
            });
        }
    }, [debouncedValue]);

    const deleteModal = async (dashboardData: any): Promise<any> => {
        const { data: newData } = await getFilter({
            body: {
                page: 0,
                size: 10,
                filters: getDeleteFilters(dashboardData),
            },
        });

        if (!newData) {
            return;
        }

        const recordCount = newData?.data?.records?.length ?? 0;
        setActiveModal(getDeleteModalState(dashboardData, recordCount));
    };

    const openModal = (dashboardData: any): void => {
        setSelectedData(dashboardData);
        deleteModal(dashboardData) as any;
    };
    React.useImperativeHandle(ref, () => ({
        handleModalAction: (dashboardData: any): void => openModal(dashboardData),
    }));

    const updateDashboardHandler = async (): Promise<void> => {
        const { error }: any = await updateDashboard({
            body: {
                status: 'ACTIVE',
                ...(selectedData?.assignType !== 'SOURCE' && {
                    assignTypeIds: selectedData?.assignTypeIds,
                }),
                assignType: selectedData?.assignType,
            },
            params: { dashboardId: selectedDashboard?.id ?? '' },
        });
        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('DASHBOARDS:DASHBOARD_UPDATE_SUCCESS'),
                    duration: 3000,
                })
            );
            setActiveModal('Delete');
        }
    };

    const deleteModalActions: ModalActions[] = [
        {
            key: 'cancel',
            testId: 'delete-dashboard-cancel-btn',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: closeModal,
        },
        {
            key: 'next',
            testId: 'delete-dashboard-next-btn',
            label: <CustomTransComponent translationKey={'COMMON:NEXT'} />,
            variant: 'contained',
            disabled: !selectedDashboard?.id,
            handleClick: updateDashboardHandler as any,
        },
    ];
    const noModalActions: ModalActions[] = [
        {
            key: 'cancel',
            testId: 'delete-dashboard-close-btn',
            label: <CustomTransComponent translationKey={'COMMON:CLOSE'} />,
            close: true,
            variant: 'outlined',
            handleClick: closeModal,
        },
    ];

    const deleteHandler = async (): Promise<void> => {
        const { error }: any = await deleteDashboard({ id: selectedData?.id ?? '' });
        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('DASHBOARDS:DASHBOARD_DELETE_SUCCESS', {
                        replace: {
                            name: selectedData?.name,
                        },
                    }),
                    duration: 3000,
                })
            );
            closeDeleteModal();
            closeModal();
            setSelectedDashboard({});
            setSelectedData(null);
        }
    };

    const getSelectedNode = (item: any): any => {
        if (item?.assignType === 'DEVICE') {
            return 'Device';
        } else if (item?.assignType === 'SOURCE') {
            return 'Source';
        }
        return 'Group';
    };

    const { onCancel: closeDeleteModal, onConfirm: confirmDeletion } = useConfirm(deleteHandler as any);
    return (
        <>
            {(isDeletingDashboard || isUpdatingDashboard) && <BackdropLoader isOpen={true} />}
            <CustomModal
                isOpen={activeModal === 'Assign'}
                type="primary"
                actions={deleteModalActions}
                customHeader={
                    <>
                        <Stack>
                            <Typography variant="body1" fontSize={'20px'} fontWeight={'600'} mb={2}>
                                <CustomTransComponent
                                    translationKey={'DASHBOARDS:DELETE_DASHBOARD.DELETE_DASHBOARD_TEXT'}
                                />
                            </Typography>
                        </Stack>
                        <Stack mt={2}>
                            <Typography variant="body1" fontWeight={'400'} mt={2}>
                                <CustomTransComponent
                                    translationKey={'DASHBOARDS:DELETE_DASHBOARD.DELETE_DEFAULT_DASHBOARD_DESC'}
                                    replace={{
                                        dataSource: selectedData?.sourceName ?? selectedData?.assignType,
                                    }}
                                />
                            </Typography>
                        </Stack>
                    </>
                }
                actionsDivider
                {...props}
            >
                {isLoading || isFetching ? (
                    <GroupHierarchySkeleton row={1} />
                ) : (
                    <List className="height-200 overflow-y-scroll">
                        <Stack
                            direction={'row'}
                            spacing={2}
                            my={2}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                            width={'100%'}
                        >
                            <TextField
                                sx={{ width: '100%' }}
                                hiddenLabel
                                id="searchKey"
                                data-testid="dashboard-delete-search"
                                placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                                size="small"
                                onChange={(e: any): void => {
                                    setSearchKey(e.target.value);
                                }}
                                value={searchKey || ''}
                                InputProps={{
                                    startAdornment: <Search id="search" sx={{ mr: '5px', color: '#727e84' }} />,
                                }}
                            />
                        </Stack>

                        {data?.data?.records?.map((item: any, index: number) => (
                            <DashboardAssignListItem
                                key={item?.id}
                                item={item}
                                index={index}
                                selectedDashboardId={selectedDashboard?.id}
                                setSelectedDashboard={setSelectedDashboard}
                                getSelectedNode={getSelectedNode}
                            />
                        ))}
                    </List>
                )}
            </CustomModal>
            <CustomModal
                data-testid="delete-dashboard-empty-modal"
                isOpen={activeModal === 'EmptyModal'}
                type="primary"
                actions={noModalActions}
                customHeader={
                    <>
                        <Stack>
                            <Typography variant="body1" fontSize={'20px'} fontWeight={'600'} mb={2}>
                                <CustomTransComponent translationKey={'DASHBOARDS:DELETE_DASHBOARD.UNABLE_DELETE'} />
                            </Typography>
                        </Stack>
                        <Stack mt={2}>
                            <Typography variant="body1" fontWeight={'400'} mt={2}>
                                <CustomTransComponent
                                    translationKey={'DASHBOARDS:DELETE_DASHBOARD.UNABLE_DELETE_TEXT'}
                                />
                            </Typography>
                        </Stack>
                    </>
                }
                noDividers
                actionsDivider
            >
                <></>
            </CustomModal>
            <DeleteModal
                onCancel={(): void => {
                    closeDeleteModal();
                    closeModal();
                }}
                confirmClick={confirmDeletion}
                isVisible={activeModal === 'Delete'}
                data-testid="delete-dashboard-confirmation-modal"
                header={<CustomTransComponent translationKey={'DASHBOARDS:DELETE_DASHBOARD.DELETE_DASHBOARD_TEXT'} />}
                noDividers
                description={
                    <CustomTransComponent
                        translationKey={'DASHBOARDS:DELETE_DASHBOARD.DELETE_DASHBOARD_DESCRIPTION'}
                        replace={{
                            name: selectedData?.name,
                        }}
                    />
                }
                deleteButtonText={'DASHBOARDS:DELETE_DASHBOARD.DELETE_DASHBOARD_TEXT'}
                isLoading={isDeletingDashboard}
            />
        </>
    );
});
